/**
 * Per-step theme color + Lucide icon registry for the /start quiz
 * (Phase 2 — Track 5.Y-cat-quiz-amplification visual treatment).
 *
 * The hero band, progress bar, selected-state ring, and card-thumbnail
 * tints all read from this single source so swapping a step's color
 * touches one row.
 *
 * Brand palette per Lock 5a + Track 5.Z3 rebalance (2026-05-12):
 *   - concerns:    brand navy (entry point — was orange; demoted)
 *   - lifestyle/sleep_band/energy/focus/memory: amber (lifestyle band)
 *   - conditions/family_history: gray-blue (medical neutral)
 *   - goal/lead-form steps: brand navy (commitment band — was orange)
 *
 * Brand orange now reserved for primary CTAs ("Continue →") and the
 * /checkout/plan price emphasis only. Selected-state cards, focus
 * rings, and theme tints across the quiz use navy.
 *
 * Interstitials inherit the THEME of the next data step they precede;
 * if the next step's color is unavailable, fall back to amber.
 */

import {
  Activity,
  Battery,
  Brain,
  CalendarDays,
  HeartHandshake,
  Mail,
  MapPin,
  Moon,
  Sparkles,
  Stethoscope,
  Target,
  User,
  Users,
  Zap,
  type LucideIcon,
} from 'lucide-react';

import type { QuestionId } from '@/lib/health-profile/quiz-state';
import type { InterstitialSlot } from '@/lib/health-profile/interstitial-content';

/** Brand-orange entry/commitment, amber lifestyle, gray-blue medical. */
export type QuizTheme = {
  /** Hex used for hero band gradient + ring + tint backgrounds. */
  themeColor: string;
  /** Lucide component shown in hero band's eyebrow row. */
  Icon: LucideIcon;
};

// Track 5.Z3 (2026-05-12): brand orange demoted from theme color to
// "primary CTA + price emphasis only." Concerns / goal / lead-form
// steps now read as navy across hero band, progress bar, and selected-
// state rings. Operator-confirmed scope.
//
// Track 5.Z4 (2026-05-12): amber demoted to navy as well — designer-
// lead observed amber selected-state reads as orange-adjacent on
// LifestyleStep (the only amber-themed COMPOSITE step where the buyer
// dwells long enough to register the color, since the 4 single-select
// dimension steps auto-advance per #209 in 250ms). Brief scoped
// LifestyleStep only; extending to all amber entries to avoid an
// amber-vs-navy visual jump mid-quiz. THEME_AMBER constant deleted —
// if visual variety returns, reintroduce with a fresh hex.
const THEME_BRAND_NAVY = '#0f1e3d';
const THEME_NEUTRAL = '#64748b'; // slate-500 — medical neutral

export const QUIZ_QUESTION_THEME: Record<QuestionId, QuizTheme> = {
  concerns:       { themeColor: THEME_BRAND_NAVY, Icon: Sparkles },
  energy:         { themeColor: THEME_BRAND_NAVY, Icon: Zap },
  focus:          { themeColor: THEME_BRAND_NAVY, Icon: Brain },
  memory:         { themeColor: THEME_BRAND_NAVY, Icon: Battery },
  sleep_band:     { themeColor: THEME_BRAND_NAVY, Icon: Moon },
  lifestyle:      { themeColor: THEME_BRAND_NAVY, Icon: Activity },
  conditions:     { themeColor: THEME_NEUTRAL,    Icon: Stethoscope },
  family_history: { themeColor: THEME_NEUTRAL,    Icon: Users },
  goal:           { themeColor: THEME_BRAND_NAVY, Icon: Target },
  // Track 3.10 (2026-05-14): 'name' merged into 'email_phone'. Icon stays
  // Mail (the most recognizable "account setup" glyph); User icon import
  // kept in case a future step revives it.
  email_phone:    { themeColor: THEME_BRAND_NAVY, Icon: Mail },
  dob_sex_zip:    { themeColor: THEME_BRAND_NAVY, Icon: MapPin },
  menstrual:      { themeColor: THEME_BRAND_NAVY, Icon: CalendarDays },
};

/** Interstitial themes mirror the ANCHOR question they sit after. */
export const QUIZ_INTERSTITIAL_THEME: Record<InterstitialSlot, QuizTheme> = {
  after_concerns: { themeColor: THEME_BRAND_NAVY, Icon: HeartHandshake },
  after_habits:   { themeColor: THEME_BRAND_NAVY, Icon: Sparkles },
  processing:     { themeColor: THEME_BRAND_NAVY, Icon: Sparkles },
};
// Track 3.13-phase-0: 'trust' entry dropped alongside its slot type.

/** Hex → rgba helper for tint backgrounds. */
export function hexToRgba(hex: string, alpha: number): string {
  let h = hex.trim().replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-f]{6}$/i.test(h)) return `rgba(255, 145, 0, ${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
